/**
 * Strict-typed payload helpers — sequence-pair invariant (no duplicates).
 *
 * One single function (`buildSequencePairs`) produces every `(stopId, sequence)`
 * payload the utility ever sends. It is the structural fix for the
 * bulk-optimization duplicate-sequence bug class:
 *
 *   v1:   `optimizedStops.map(s => ({ stopId: s.id, sequence: i + 1 }))`
 *         — duplicate `s.id` ⇒ duplicate sequences ⇒ BE corrupts the route.
 *   v2:   `buildSequencePairs(optimizedStops, 'id')`
 *         — duplicates are detected, dropped, and surfaced as a typed
 *           `DUPLICATE_STOP_IN_PAYLOAD` error before the mutation fires.
 *
 * Helpers in this file are pure, no I/O, no mutation of inputs. The only
 * runtime data structure is a `Set` for the dedupe walk.
 */

import { ok, err, type Result } from 'types/entities/shared/result'
import { asStopId, type StopId } from 'types/entities/shared/ids'
import type { RouteOpError, SequencePair } from 'types/modules/routing/Result'

// ── Mode / shared types ─────────────────────────────────────────────────

/**
 * `strict` — any dropped duplicate aborts the commit (`DUPLICATE_STOP_IN_PAYLOAD`).
 * `permissive` — duplicates are dropped silently and the cleaned pairs are returned;
 *  the caller is expected to log a warning (the utility's executor does this).
 *
 * Default everywhere is `strict`. Bulk paths can opt into `permissive` if the
 * source data is known-noisy and the user-facing UX is "best-effort".
 */
export type PayloadMode = 'strict' | 'permissive'

export interface BuildSequencePairsResult {
  readonly pairs: ReadonlyArray<SequencePair>
  readonly droppedDuplicates: ReadonlyArray<StopId>
  readonly droppedFalsy: number
}

/**
 * Bulk batch shape — mirrors v1's `useGetBEBatches`/`useGetBEBatchesOptimizedRoute`
 * output, the BE-known `bulkMoveStops` request shape. Strings here intentionally;
 * the BE accepts plain stop ids and the brand is unwrapped at the edge.
 */
export interface BulkBatch {
  readonly destinationRouteId: string
  readonly stops: ReadonlyArray<string>
  readonly serviceDate: string
  readonly targetStopId: string | null
  readonly updateMasterSequence: boolean
}

// ── Core helper — Invariant 1 ───────────────────────────────────────────

/**
 * Walks `rows` once, dedupes by `idKey` via `Set`, re-numbers monotonically
 * from `startSequence`. Falsy ids are dropped silently and counted.
 *
 * **Replaces v1**: `toSequencePairs`, `buildDailyAssignPayload`,
 * `buildMasterAssignPayload`, `buildDailyResequencePayload`,
 * `buildMasterResequencePayload`, and every inline
 * `.map((x, i) => ({ stopId: x.id, sequence: i + 1 }))` pattern.
 *
 * Returns the kept pairs + dropped duplicates; the caller decides whether to
 * abort or warn (see `buildDailyResequencePayload` / `buildMasterResequencePayload`).
 *
 * @param rows ordered source rows; never mutated
 * @param idKey field on each row that holds the BE id
 * @param startSequence first sequence value to emit (default 1)
 */
export function buildSequencePairs<T>(rows: ReadonlyArray<T>, idKey: keyof T, startSequence = 1): BuildSequencePairsResult {
  const seen = new Set<string>()
  const pairs: SequencePair[] = []
  const droppedDuplicates: StopId[] = []
  let droppedFalsy = 0

  for (const row of rows) {
    const raw = row[idKey]
    if (raw == null || raw === '') {
      droppedFalsy += 1
      continue
    }
    if (typeof raw !== 'string') {
      droppedFalsy += 1
      continue
    }
    if (seen.has(raw)) {
      droppedDuplicates.push(asStopId(raw))
      continue
    }
    seen.add(raw)
    pairs.push({ stopId: asStopId(raw), sequence: startSequence + pairs.length })
  }

  return {
    pairs,
    droppedDuplicates,
    droppedFalsy,
  }
}

// ── Mode-aware Result wrappers ──────────────────────────────────────────

interface BuildResequenceOptions {
  readonly mode?: PayloadMode
  readonly startSequence?: number
}

/**
 * Daily resequence payload builder. In `strict` mode (default), any dropped
 * duplicate is fatal and surfaces as `DUPLICATE_STOP_IN_PAYLOAD`. In
 * `permissive` mode, duplicates are dropped and the cleaned pairs returned.
 */
export function buildDailyResequencePayload<T>(
  rows: ReadonlyArray<T>,
  idKey: keyof T,
  options: BuildResequenceOptions = {}
): Result<{ readonly pairs: ReadonlyArray<SequencePair> }, RouteOpError> {
  return buildResequencePayload(rows, idKey, options)
}

/**
 * Master resequence payload builder. Same semantics as the daily variant; the
 * function exists as a separate symbol so call sites read clearly and we can
 * diverge later (e.g. master-only invariants) without churning callers.
 */
export function buildMasterResequencePayload<T>(
  rows: ReadonlyArray<T>,
  idKey: keyof T,
  options: BuildResequenceOptions = {}
): Result<{ readonly pairs: ReadonlyArray<SequencePair> }, RouteOpError> {
  return buildResequencePayload(rows, idKey, options)
}

function buildResequencePayload<T>(
  rows: ReadonlyArray<T>,
  idKey: keyof T,
  options: BuildResequenceOptions
): Result<{ readonly pairs: ReadonlyArray<SequencePair> }, RouteOpError> {
  const mode: PayloadMode = options.mode ?? 'strict'
  const { pairs, droppedDuplicates } = buildSequencePairs(rows, idKey, options.startSequence ?? 1)

  if (droppedDuplicates.length > 0 && mode === 'strict') {
    return err<RouteOpError>({
      kind: 'DUPLICATE_STOP_IN_PAYLOAD',
      duplicates: droppedDuplicates,
    })
  }

  return ok({ pairs })
}

// ── Bulk batch builders ─────────────────────────────────────────────────

interface BulkRow {
  readonly id: string
  readonly routeId?: string
  readonly date?: string
  readonly isChanged?: boolean
}

interface BuildBulkInsertBatchInput {
  readonly rows: ReadonlyArray<BulkRow>
  readonly destinationRouteId: string
  readonly serviceDate: string
  readonly updateMasterSequence?: boolean
}

interface BuildBulkResequenceBatchInput {
  readonly rows: ReadonlyArray<BulkRow>
  readonly destinationRouteId: string
  readonly serviceDate: string
  readonly updateMasterSequence?: boolean
}

interface BuildBulkFullReorderBatchInput {
  readonly orderedStops: ReadonlyArray<{ readonly id: string }>
  readonly destinationRouteId: string
  readonly serviceDate: string
  readonly updateMasterSequence?: boolean
  readonly mode?: PayloadMode
}

/**
 * Insert-style bulk batch — port of `useGetBEBatches`. Walks `rows` and emits
 * one batch per contiguous run of "new" rows (where `routeId !== destinationRouteId`).
 * Dedupes the stop ids inside each batch via `buildSequencePairs` so a duplicate
 * id in the input cannot reach the BE.
 */
export function buildBulkInsertBatch(input: BuildBulkInsertBatchInput): ReadonlyArray<BulkBatch> {
  const { rows, destinationRouteId, serviceDate, updateMasterSequence = false } = input
  return walkBulkRuns(rows, row => row.routeId !== destinationRouteId, {
    destinationRouteId,
    serviceDate,
    updateMasterSequence,
  })
}

/**
 * Resequence-style bulk batch — port of `useGetBEBatchesResequenceVariant`. Same
 * walk semantics but keys on `row.isChanged`.
 */
export function buildBulkResequenceBatch(input: BuildBulkResequenceBatchInput): ReadonlyArray<BulkBatch> {
  const { rows, destinationRouteId, serviceDate, updateMasterSequence = false } = input
  return walkBulkRuns(rows, row => row.isChanged === true, {
    destinationRouteId,
    serviceDate,
    updateMasterSequence,
  })
}

/**
 * Full-reorder bulk batch — port of `useGetBEBatchesOptimizedRoute`. Emits a
 * single batch containing every stop id in the optimized order. **Routes the
 * stop list through `buildSequencePairs` before emitting** — this is the
 * structural kill for the duplicate-sequence bug at
 * `useGetBEBatchesOptimizedRoute.js:26`.
 *
 * Returns a `Result` so a duplicate id in `optimizedStops` aborts in `strict`
 * mode (default) instead of leaking to the BE.
 */
export function buildBulkFullReorderBatch(input: BuildBulkFullReorderBatchInput): Result<ReadonlyArray<BulkBatch>, RouteOpError> {
  const { orderedStops, destinationRouteId, serviceDate, updateMasterSequence = false } = input
  const mode: PayloadMode = input.mode ?? 'strict'

  if (orderedStops.length === 0) return ok([])

  const { pairs, droppedDuplicates } = buildSequencePairs(orderedStops, 'id')

  if (droppedDuplicates.length > 0 && mode === 'strict') {
    return err<RouteOpError>({
      kind: 'DUPLICATE_STOP_IN_PAYLOAD',
      duplicates: droppedDuplicates,
    })
  }

  const batch: BulkBatch = {
    destinationRouteId,
    stops: pairs.map(p => p.stopId as string),
    serviceDate,
    targetStopId: null,
    updateMasterSequence,
  }

  return ok([batch])
}

// ── Internals ───────────────────────────────────────────────────────────

/**
 * Walks `rows` and emits a `BulkBatch` per contiguous run where `predicate` is
 * true. Rows that fail the predicate close the current batch and become the
 * `targetStopId` for the next one — same shape v1's hooks emit.
 *
 * Inside each run, ids are deduped via `Set` so a duplicate id can't double-up
 * the BE batch.
 */
function walkBulkRuns(
  rows: ReadonlyArray<BulkRow>,
  predicate: (row: BulkRow) => boolean,
  batchTemplate: { readonly destinationRouteId: string; readonly serviceDate: string; readonly updateMasterSequence: boolean }
): ReadonlyArray<BulkBatch> {
  const batches: BulkBatch[] = []
  let lastStop: string | null = null
  let currentRunIds: string[] | null = null
  let currentSeen: Set<string> | null = null
  let currentTargetStopId: string | null = null

  const flush = () => {
    if (currentRunIds && currentRunIds.length > 0) {
      batches.push({
        ...batchTemplate,
        stops: currentRunIds,
        targetStopId: currentTargetStopId,
      })
    }
    currentRunIds = null
    currentSeen = null
    currentTargetStopId = null
  }

  for (const row of rows) {
    if (predicate(row)) {
      if (!currentRunIds || !currentSeen) {
        currentRunIds = []
        currentSeen = new Set<string>()
        currentTargetStopId = lastStop
      }
      if (row.id && !currentSeen.has(row.id)) {
        currentSeen.add(row.id)
        currentRunIds.push(row.id)
      }
    } else {
      flush()
      lastStop = row.id ?? lastStop
    }
  }
  flush()

  return batches
}
